// src/components/admin/compensation/AdminCompensationSettings.jsx
import React, { useEffect, useState } from 'react';
import { FiSettings, FiPlus, FiEdit, FiToggleLeft, FiToggleRight, FiCheck, FiX } from 'react-icons/fi';
import { tenantDataService } from '../../../services/tenantDataService';
import { isAdvancedPlansEnabled, createDefaultPlansFromRoleRates, autoAssignDefaultPlans } from '../../../services/compPlanService';
import CompensationPlanWizard from './CompensationPlanWizard';
import CompensationSetupGuide from './CompensationSetupGuide';
import toast from 'react-hot-toast';

export default function AdminCompensationSettings() {
  const [enabled, setEnabled] = useState(false);
  const [plans, setPlans] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [isEnabled, plansList] = await Promise.all([
        isAdvancedPlansEnabled(),
        tenantDataService.getAll('compensationPlans')
      ]);
      
      setEnabled(isEnabled);
      setPlans(plansList.sort((a, b) => String(a.name).localeCompare(String(b.name))));
    } catch (error) {
      console.error('Error loading compensation data:', error);
      toast.error('Failed to load compensation settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleEnabled = async () => {
    try {
      const settings = await tenantDataService.getById('settings', 'clinic');
      const next = !enabled;
      
      await tenantDataService.update('settings', 'clinic', {
        ...settings,
        compensation: { 
          ...(settings?.compensation || {}), 
          enableAdvancedPlans: next 
        }
      });
      
      setEnabled(next);
      
      // On first enable: create default plans if none exist
      if (next && plans.length === 0) {
        await createDefaultPlansFromRoleRates();
        const defaultPlans = await tenantDataService.getAll('compensationPlans');
        await autoAssignDefaultPlans(defaultPlans);
        setPlans(defaultPlans.sort((a, b) => String(a.name).localeCompare(String(b.name))));
        toast.success('Default compensation plans created from existing role rates');
      }
      
      toast.success(`Advanced compensation plans ${next ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling compensation plans:', error);
      toast.error('Failed to update compensation settings');
    }
  };

  const togglePlanActive = async (planId, currentActive) => {
    try {
      await tenantDataService.update('compensationPlans', planId, { 
        active: !currentActive, 
        updatedAt: new Date() 
      });
      
      setPlans(prev => prev.map(p => 
        p.id === planId ? { ...p, active: !p.active } : p
      ));
      
      toast.success(`Plan ${!currentActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error toggling plan status:', error);
      toast.error('Failed to update plan status');
    }
  };

  const handleWizardClose = async (saved) => {
    setShowWizard(false);
    setEditingPlan(null);
    
    if (saved) {
      await loadData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Loading compensation settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Setup Guide */}
      <CompensationSetupGuide />
      
      {/* Feature Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <FiSettings className="text-blue-600" />
              Advanced Compensation Plans
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Toggle on to use plan-based payouts. Toggle off to keep role-percentage payouts.
            </p>
          </div>
          <button 
            onClick={toggleEnabled}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
              enabled 
                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {enabled ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
            {enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        
        {!enabled && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Current mode:</strong> Using role-based percentage rates from Financial Settings. 
              Enable advanced plans to create custom compensation structures with thresholds, tiers, and deductions.
            </p>
          </div>
        )}
      </div>

      {/* Plans Management */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Compensation Plans</h3>
          <button 
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            onClick={() => { 
              setEditingPlan(null); 
              setShowWizard(true); 
            }}
          >
            <FiPlus size={16} />
            Create Plan
          </button>
        </div>

        {plans.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-2">No compensation plans created yet</div>
            <p className="text-sm text-gray-400">
              {enabled 
                ? "Create your first plan to start using advanced compensation features" 
                : "Enable advanced plans to create custom compensation structures"
              }
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {plans.map(plan => (
              <div key={plan.id} className="rounded-xl border border-gray-200 p-4 hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{plan.name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Basis: {plan.commissionBasis} • Scope: {plan.scope}
                    </div>
                    {plan.threshold?.type && plan.threshold.type !== 'none' && (
                      <div className="text-xs text-gray-500">
                        Threshold: {plan.threshold.type} (£{plan.threshold.amount})
                      </div>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    plan.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {plan.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="flex gap-2">
                  <button 
                    className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    onClick={() => { 
                      setEditingPlan(plan); 
                      setShowWizard(true); 
                    }}
                  >
                    <FiEdit size={14} />
                    Edit
                  </button>
                  <button 
                    className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      plan.active
                        ? 'border-red-200 text-red-700 hover:bg-red-50'
                        : 'border-green-200 text-green-700 hover:bg-green-50'
                    }`}
                    onClick={() => togglePlanActive(plan.id, plan.active)}
                  >
                    {plan.active ? <FiX size={14} /> : <FiCheck size={14} />}
                    {plan.active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <CompensationPlanWizard
          initialPlan={editingPlan}
          onClose={handleWizardClose}
        />
      )}
    </div>
  );
}
